import type React from 'react';
import type { IconName } from '@customerio/pluma-icons';
import { generatePath } from '@/services/router/generate-path';
import pipelinesLogomark from '@/assets/images/logos/logomark-data-pipelines.svg';
import segmentIcon from '@/assets/images/integrations/segment.svg';
import iosIcon from '@/assets/images/sdk-logos/ios.svg';
import androidIcon from '@/assets/images/sdk-logos/android.svg';
import reactNativeIcon from '@/assets/images/sdk-logos/react-native.svg';
import expoIcon from '@/assets/images/sdk-logos/expo.svg';
import flutterIcon from '@/assets/images/sdk-logos/flutter.svg';
import javascriptIcon from '@/assets/images/integrations/javascript.svg';
import snowflakeIcon from '@/assets/images/integrations/snowflake.svg';
import bigqueryIcon from '@/assets/images/integrations/bigquery.svg';
import redshiftIcon from '@/assets/images/integrations/redshift.svg';
import typeformIcon from '@/assets/images/integrations/typeform.svg';
import jotformIcon from '@/assets/images/integrations/jotform.svg';
import facebookIcon from '@/assets/images/integrations/facebook.svg';
import zapierIcon from '@/assets/images/integrations/zapier.svg';
import zendeskIcon from '@/assets/images/integrations/zendesk.svg';
import airtableIcon from '@/assets/images/integrations/airtable.svg';
import googleSheetsIcon from '@/assets/images/integrations/google-sheets.svg';

export type ActionIcon =
  | { type: 'image'; value: string; alt?: string }
  | { type: 'icon'; value: IconName }
  | { type: 'text'; value: string };

export interface AddPeopleAction {
  title?: string;
  description: React.ReactNode;
  icons: ActionIcon[];
  href: string;
  recommended?: boolean;
}

export function getIntegrationActions(
  workspaceId: number,
  getSourceAddHref: (source: string) => string,
): AddPeopleAction[] {
  return [
    {
      title: 'Customer.io API',
      description: 'Integrate your app with our RESTful API. Requires development resources.',
      icons: [{ type: 'image', value: pipelinesLogomark, alt: 'Customer.io Pipelines' }],
      href: getSourceAddHref('http'),
      recommended: true,
    },
    {
      title: 'Mobile SDKs',
      description: 'Integrate your mobile apps without writing an integration from scratch.',
      icons: [
        { type: 'image', value: iosIcon, alt: 'iOS' },
        { type: 'image', value: androidIcon, alt: 'Android' },
        { type: 'image', value: reactNativeIcon, alt: 'React Native' },
        { type: 'image', value: expoIcon, alt: 'Expo' },
        { type: 'image', value: flutterIcon, alt: 'Flutter' },
      ],
      href: generatePath(
        '/workspaces/:workspaceId/journeys/integrations/all/directory',
        { workspaceId },
        { selectedCategory: 'Mobile' },
      ),
      recommended: true,
    },
    {
      // The `journeys` spelling is left as-is. Hydra owns `/integrations/:type`
      // unconditionally now (FPS-3724) and claims the `journeys` twin too, which forwards here,
      // so both spellings work and this one costs a single client-side hop. It stays because
      // every other integrations href in this file uses the same spelling; re-pointing one of
      // them alone would make the file inconsistent.
      title: 'Segment',
      description: 'Integrate using Customer.io as a Segment destination action.',
      icons: [{ type: 'image', value: segmentIcon, alt: 'Segment' }],
      href: generatePath('/workspaces/:workspaceId/journeys/integrations/:type', { workspaceId, type: 'Segment' }),
    },
    {
      title: 'JavaScript snippet',
      description: 'Drop a snippet in your site to add new people without writing your own code.',
      icons: [{ type: 'image', value: javascriptIcon, alt: 'JavaScript' }],
      href: getSourceAddHref('javascript'),
    },
    {
      title: 'Database',
      description: 'Sync people and attributes from a database on a regular interval.',
      icons: [
        { type: 'image', value: snowflakeIcon, alt: 'Snowflake' },
        { type: 'image', value: bigqueryIcon, alt: 'BigQuery' },
        { type: 'image', value: redshiftIcon, alt: 'Redshift' },
        { type: 'text', value: '5+' },
      ],
      href: generatePath(
        '/workspaces/:workspaceId/journeys/integrations/all/directory',
        { workspaceId },
        { selectedCategory: 'Database & data warehouse', selectedType: 'Data in' },
      ),
    },
    {
      title: 'Form',
      description: 'Add people when they submit a form from Typeform, Jotform, etc.',
      icons: [
        { type: 'image', value: typeformIcon, alt: 'Typeform' },
        { type: 'image', value: jotformIcon, alt: 'Jotform' },
        { type: 'image', value: facebookIcon, alt: 'Facebook' },
        { type: 'icon', value: 'form' },
      ],
      href: generatePath(
        '/workspaces/:workspaceId/journeys/integrations/all/directory',
        { workspaceId },
        { query: 'form' },
      ),
    },
    {
      description: (
        <>
          Looking for other tools? <span style={{ textDecoration: 'underline' }}>View all integrations</span>.
        </>
      ),
      icons: [
        { type: 'image', value: zapierIcon, alt: 'Zapier' },
        { type: 'image', value: zendeskIcon, alt: 'Zendesk' },
        { type: 'image', value: airtableIcon, alt: 'Airtable' },
        { type: 'text', value: '99+' },
      ],
      href: generatePath(
        '/workspaces/:workspaceId/journeys/integrations/all/directory',
        { workspaceId },
        { selectedGroup: '' },
      ),
    },
  ];
}

export function getManualActions(workspaceId: number, addPersonHref: string): AddPeopleAction[] {
  return [
    {
      title: 'CSV',
      description: 'Import a list of people from another system with a CSV or Google Sheet.',
      icons: [
        { type: 'icon', value: 'file-csv' },
        { type: 'image', value: googleSheetsIcon, alt: 'Google Sheets' },
      ],
      href: generatePath('/workspaces/:workspaceId/journeys/imports/new', { workspaceId }, { type: 'people' }),
    },
    {
      title: 'Add a single person',
      description: 'Manually add individuals that slip through other integration methods.',
      icons: [{ type: 'icon', value: 'person' }],
      href: addPersonHref,
    },
    {
      title: 'Add sample data',
      description: 'Get started quickly with people and event sample data.',
      icons: [{ type: 'icon', value: 'feature-launch' }],
      href: generatePath(
        '/workspaces/:workspaceId/journeys/imports/new',
        { workspaceId },
        { highlightSampleData: 'true' },
      ),
    },
  ];
}
